import styled from '@emotion/styled';
import { ObservablePrimitive } from '@legendapp/state';
import * as icon from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';

export const useAudio = (
  url: string,
  currentlyPlayingUrl: ObservablePrimitive<string>,
  options?: { probe?: boolean },
): [boolean, () => void, number, number, string, (time: number) => void] => {
  const [audio] = useState(() => {
    const a = new Audio(url);
    a.preload = options?.probe && url ? 'metadata' : 'none';
    return a;
  });
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(audio.duration);
  const [error, setError] = useState('');
  const toggle = () => setPlaying(!playing);

  useEffect(() => {
    if (playing) {
      currentlyPlayingUrl.set(url);
      audio.play().catch((e) => {
        if (e.name !== 'AbortError') {
          setPlaying(false);
          setError(e.message || 'Failed to play audio');
        }
      });
    } else {
      audio.pause();
    }
  }, [playing]);

  const endedListener = () => setPlaying(false);

  const metadataListener = () => {
    setAudioDuration(audio.duration);
  };

  const timeListener = () => {
    setCurrentTime(audio.currentTime);
  };

  const onError = () => {
    const msg =
      audio?.error?.message || (audio?.error?.code ? `Audio error (code ${audio.error.code})` : '');
    if (msg) {
      setError(msg);
    }
  };

  useEffect(() => {
    audio.addEventListener('ended', endedListener);
    audio.addEventListener('loadedmetadata', metadataListener);
    audio.addEventListener('timeupdate', timeListener);
    audio.addEventListener('error', onError);

    if (options?.probe && url) {
      audio.load();
    }

    return () => {
      audio.removeEventListener('ended', endedListener);
      audio.removeEventListener('loadedmetadata', metadataListener);
      audio.removeEventListener('timeupdate', timeListener);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, []);

  useEffect(() => {
    return currentlyPlayingUrl.onChange((newUrl) => {
      if (newUrl !== url && playing) {
        setPlaying(false);
      }
    });
  }, [playing]);

  const seek = (time: number) => {
    audio.currentTime = time;
    setCurrentTime(time);
  };

  return [playing, toggle, currentTime, audioDuration, error, seek];
};

export const probeAudioFile = (file: File, timeoutMs = 5000): Promise<string | null> => {
  const objectUrl = URL.createObjectURL(file);
  return probeAudioUrl(objectUrl, timeoutMs).finally(() => URL.revokeObjectURL(objectUrl));
};

export const probeAudioUrl = (url: string, timeoutMs = 5000): Promise<string | null> =>
  new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const audio = new Audio(url);
    audio.preload = 'metadata';
    let settled = false;
    const settle = (result: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute('src');
      audio.load();
      resolve(result);
    };
    audio.onloadedmetadata = () => settle(null);
    audio.onerror = () => {
      const msg =
        audio.error?.message ||
        (audio.error?.code ? `Audio error (code ${audio.error.code})` : 'Audio not reachable');
      settle(msg);
    };
    audio.load();
    const timer = setTimeout(() => settle('Audio URL timed out'), timeoutMs);
  });

export interface LabeledUrl {
  label: string;
  url: string;
}

export const validateIvrUrls = async (
  urls: LabeledUrl[],
): Promise<{ valid: true } | { valid: false; message: string }> => {
  const emptyUrls = urls.filter((u) => !u.url.trim());
  if (emptyUrls.length > 0) {
    return {
      valid: false,
      message: `${emptyUrls.map((u) => u.label).join(', ')} URL${
        emptyUrls.length > 1 ? 's are' : ' is'
      } required`,
    };
  }

  const results = await Promise.all(urls.map((u) => probeAudioUrl(u.url)));
  const failures = urls.filter((_, i) => results[i] !== null);
  if (failures.length > 0) {
    return {
      valid: false,
      message: `Cannot save: ${failures.map((f) => f.label).join(', ')} URL${
        failures.length > 1 ? 's are' : ' is'
      } not reachable`,
    };
  }

  return { valid: true };
};

const StyledIconButton = styled(IconButton)`
  width: 34px;
  height: 34px;
  top: 3px;
  left: 3px;
  bottom: 0;
  right: 0;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiTouchRipple-child {
    background-color: #1976d2;
  }

  &:hover {
    background-color: #e9e9ff;
  }
`;

export const AudioPlayButton: FC<{
  url: string;
  currentlyPlayingUrl: ObservablePrimitive<string>;
}> = ({ url, currentlyPlayingUrl }) => {
  const [playing, toggle, currentTime, duration] = useAudio(url, currentlyPlayingUrl);

  return (
    <PlayPauseButton
      currentTime={currentTime}
      duration={duration}
      onClick={toggle}
      playing={playing}
    />
  );
};

const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AudioPreviewButton: FC<{
  url: string;
  currentlyPlayingUrl: ObservablePrimitive<string>;
  showSeekBar?: boolean;
}> = ({ url, currentlyPlayingUrl, showSeekBar }) => {
  const [playing, toggle, currentTime, duration, error, seek] = useAudio(
    url || '',
    currentlyPlayingUrl,
    { probe: true },
  );
  const disabled = !url;
  const hasError = !!error && !!url;
  const hasDuration = !isNaN(duration) && isFinite(duration) && duration > 0;

  const handleClick = () => {
    if (!hasError) toggle();
  };

  return (
    <Tooltip title={hasError ? 'Preview not reachable' : ''} disableHoverListener={!hasError}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          opacity: disabled ? 0.3 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          cursor: hasError ? 'default' : 'pointer',
          ...(showSeekBar && {
            '& > .MuiBox-root:first-of-type': { marginTop: 0 },
          }),
          ...(hasError && {
            '& .MuiSvgIcon-root': { color: 'error.main' },
            '& .MuiCircularProgress-root': { color: 'error.main' },
          }),
        }}
      >
        <PlayPauseButton
          currentTime={currentTime}
          duration={duration}
          onClick={handleClick}
          playing={hasError ? false : playing}
        />
        {showSeekBar && (
          <>
            <Slider
              size="small"
              value={hasDuration ? currentTime : 0}
              max={hasDuration ? duration : 1}
              onChange={(_e, v) => seek(v as number)}
              sx={{ width: 120 }}
              disabled={!hasDuration || hasError}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', minWidth: 80, textAlign: 'right' }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

export const PlayPauseButton = ({
  currentTime,
  duration,
  onClick,
  playing,
}: {
  duration: number;
  onClick: () => void;
  playing: boolean;
  currentTime: number;
}) => {
  let value = !isNaN(duration) && duration > 0 ? Math.round((currentTime / duration) * 100) : 0;
  value = value === 100 ? 0 : value;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', marginTop: 1 }}>
      <CircularProgress variant="determinate" value={value} />
      <StyledIconButton onClick={onClick} color="primary">
        {playing ? <icon.PauseCircle /> : <icon.PlayCircle />}
      </StyledIconButton>
    </Box>
  );
};
